import type { DetectorResult, ScanInput } from "../core/types.js";
import type { XPost } from "../sources/x.js";
import type { HeliusTransaction } from "../sources/helius.js";

/**
 * Cross-source correlation detector.
 *
 * The hardest signal to fake: do the X posting timeline and the on-chain
 * activity timeline correlate? An autonomous agent that is actually running
 * the claimed wallet will show synchronized bursts. A human operator
 * manually posting will show uncorrelated timelines.
 *
 * Score is 0-100 where higher = stronger correlation = more autonomous.
 * Uncorrelated timelines (two independent actors) score near 0, which
 * usually flips the verdict to HYBRID.
 */
export function correlationDetector(
  posts: XPost[],
  txs: HeliusTransaction[],
  target: ScanInput
): DetectorResult {
  const notes: string[] = [];

  if (posts.length < 10 || txs.length < 10) {
    notes.push(`insufficient data (posts=${posts.length}, txs=${txs.length})`);
    return {
      detector: "correlation",
      score: 50,
      samples: posts.length + txs.length,
      notes,
      runtime_ms: 0,
    };
  }

  // Bin by hour, count events per bin.
  const postsByHour = new Map<number, number>();
  const txsByHour = new Map<number, number>();

  const bucket = (t: number) => Math.floor(t / 3600);

  for (const p of posts) {
    const b = bucket(p.created_at);
    postsByHour.set(b, (postsByHour.get(b) ?? 0) + 1);
  }
  for (const t of txs) {
    const b = bucket(t.timestamp);
    txsByHour.set(b, (txsByHour.get(b) ?? 0) + 1);
  }

  // Build aligned vectors over the union of hours.
  const allHours = new Set([...postsByHour.keys(), ...txsByHour.keys()]);
  const postVec: number[] = [];
  const txVec: number[] = [];
  for (const h of allHours) {
    postVec.push(postsByHour.get(h) ?? 0);
    txVec.push(txsByHour.get(h) ?? 0);
  }

  // Pearson correlation coefficient.
  const pearson = pearsonCorrelation(postVec, txVec);
  notes.push(`pearson r = ${pearson.toFixed(3)}`);

  // Activity window overlap: how much of the X activity window overlaps
  // with the on-chain window?
  const postSpan = maxOf(posts.map((p) => p.created_at)) - minOf(posts.map((p) => p.created_at));
  const txSpan = maxOf(txs.map((t) => t.timestamp)) - minOf(txs.map((t) => t.timestamp));
  const postStart = minOf(posts.map((p) => p.created_at));
  const txStart = minOf(txs.map((t) => t.timestamp));
  const overlapStart = Math.max(postStart, txStart);
  const overlapEnd = Math.min(postStart + postSpan, txStart + txSpan);
  const overlap = Math.max(0, overlapEnd - overlapStart);
  const overlapRatio = overlap / Math.max(postSpan, txSpan, 1);
  notes.push(`timeline overlap: ${(overlapRatio * 100).toFixed(1)}%`);

  // Transform pearson [-1, 1] -> [0, 100] with emphasis on positive correlation.
  const corrScore = Math.max(0, pearson) * 100;
  const overlapScore = overlapRatio * 100;

  const score = corrScore * 0.7 + overlapScore * 0.3;

  // Tag the target for debugging in CLI output.
  notes.push(`target: @${target.handle}, ${target.wallet.slice(0, 8)}...`);

  return {
    detector: "correlation",
    score: Math.round(Math.max(0, Math.min(100, score))),
    samples: posts.length + txs.length,
    notes,
    runtime_ms: 0,
  };
}

function pearsonCorrelation(x: number[], y: number[]): number {
  if (x.length !== y.length || x.length === 0) return 0;
  const n = x.length;
  const meanX = x.reduce((a, b) => a + b, 0) / n;
  const meanY = y.reduce((a, b) => a + b, 0) / n;
  let num = 0;
  let denX = 0;
  let denY = 0;
  for (let i = 0; i < n; i++) {
    const dx = x[i]! - meanX;
    const dy = y[i]! - meanY;
    num += dx * dy;
    denX += dx * dx;
    denY += dy * dy;
  }
  const denom = Math.sqrt(denX * denY);
  return denom === 0 ? 0 : num / denom;
}

function minOf(arr: number[]): number {
  return arr.reduce((a, b) => (a < b ? a : b), arr[0] ?? 0);
}
function maxOf(arr: number[]): number {
  return arr.reduce((a, b) => (a > b ? a : b), arr[0] ?? 0);
}
